import React from 'react'
import { withApollo } from 'react-apollo'

import { compose, withHandlers, withProps, withState } from 'recompose'

import AgreementDoc from 'components/AgreementDoc'
import { sendToSentry } from 'utils/errors'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'

import { acceptanceDocQuery } from './query'
import { AgreementData, AgreementDataDocument, AgreementTypes } from './types'

type AddedProps = {
  currentAgreement: AgreementDataDocument
}

type StateProps = {
  isModalOpen: boolean
  setModalOpen: () => void
  isLoading: boolean
  setLoading: (isLoading: boolean) => void
  agreementData: AgreementData
  setAgreementData: (agreementData: AgreementData) => void
  currentType: 'program_agreement'
  setCurrentType: (currentType: string) => void
}

type ApolloProps = {
  client: any
}

type HandlerProps = {
  loadDataWithGQL: () => Promise<void>
  openAgreementModal: (agreementType: string) => void
  closeAgreementModal: () => void
  getAgreementIds: (partnerName: string) => []
}

type InnerProps = AddedProps & StateProps & ApolloProps & HandlerProps & WithFlowRouterProps

export type WithAgreementsProps = InnerProps

export default compose<InnerProps, {}>(
  withApollo,
  withFlowRouter,
  withState('isModalOpen', 'setModalOpen', false),
  withState('isLoading', 'setLoading', true),
  withState('agreementData', 'setAgreementData', null),
  withState('currentType', 'setCurrentType', null),
  withHandlers<InnerProps, {}>({
    loadDataWithGQL: ({ setAgreementData, setLoading, client }) => async () => {
      setLoading(true)

      const promises = AgreementTypes.map(type =>
        client.query({
          query: acceptanceDocQuery(type),
        }),
      )

      try {
        const data = await Promise.all(promises)
        const nestedData = data.map(res => res.data.acceptanceDocument)

        const agreementData = nestedData.map(({ url, ...rest }) => ({
          ...rest,
          document: <AgreementDoc src={url} />,
        }))

        setAgreementData(agreementData)
      } catch (error) {
        sendToSentry(error, {
          name: 'ParseError',
          message: 'Agreement documents could not be loaded and parsed.',
        })
      }

      setLoading(false)
    },
  }),
  withHandlers({
    openAgreementModal: ({ setCurrentType, setModalOpen }) => type => event => {
      event.preventDefault()
      setCurrentType(type)
      setModalOpen(true)
    },
    closeAgreementModal: ({ setModalOpen, setCurrentType }) => () => {
      setCurrentType(null)
      setModalOpen(false)
    },
    getAgreementIds: ({ agreementData }) => partnerName => {
      if (!agreementData) {
        return []
      }

      const agreementTypes = ['program_agreement', 'eft_agreement', 'acorns_privacy_policy']

      if (partnerName) {
        agreementTypes.push('partner_agreement')
      }

      return (agreementData || []).filter(a => agreementTypes.includes(a.type)).map(a => a.id)
    },
  }),
  withProps(({ agreementData, currentType }) => {
    let currentAgreement
    if (!agreementData) {
      currentAgreement = {
        name: 'Terms and Conditions',
        type: 'error',
        document: (
          <div style={{ textAlign: 'center', paddingTop: '30px', height: '100%' }}>
            {' '}
            Click to see{' '}
            <a
              href="https://www.acorns.com/terms/"
              style={{ color: '#1A1A1A' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              terms and conditions
            </a>
          </div>
        ),
      }
    }
    if (agreementData && currentType) {
      currentAgreement = agreementData.find(a => a.type === currentType)
    }
    return {
      currentAgreement,
    }
  }),
)
